import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './StaticPages.css';
import './LandingPage.css';
import { cloudinaryOptimize } from '../utils/cloudinaryOptimize';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LocationPage = ({ city, state, suburb, region, population, mediaRent, description, nearby }) => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState({ count: 0, avgRent: 0, minNet: 0 });

  useEffect(() => {
    axios.get(`${API}/properties`).then(res => {
      const all = res.data;
      // Match by city or nearby suburbs
      const searchTerms = [city.toLowerCase(), ...(nearby || []).map(s => s.toLowerCase())];
      const local = all.filter(p =>
        searchTerms.some(t => (p.city || '').toLowerCase().includes(t)) &&
        (p.available_bedrooms || 0) > 0
      );
      local.sort((a, b) => a.weekly_rent_per_person - b.weekly_rent_per_person);
      setProperties(local.slice(0, 6));

      const withRent = all.filter(p =>
        searchTerms.some(t => (p.city || '').toLowerCase().includes(t)) &&
        (p.weekly_rent_per_person || 0) > 0
      );
      if (withRent.length > 0) {
        const avg = Math.round(withRent.reduce((s, p) => s + p.weekly_rent_per_person, 0) / withRent.length);
        const minR = Math.min(...withRent.map(p => p.weekly_rent_per_person));
        const sCRA = Math.min(73.23, Math.max(0, 0.75 * (minR - 74.10)));
        setStats({ count: withRent.length, avgRent: avg, minNet: Math.round(minR - sCRA) });
      }
    }).catch(() => {});
  }, [city]);

  const h1 = `${city} Shared Housing for Age Pensioners`;

  return (
    <div className="static-page">
      <header className="static-header" style={{ position: 'sticky', top: 0, zIndex: 100, background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div className="header-container">
          <div className="logo-section" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
            <img src="/logo.png" alt="House Sharing Seniors" className="logo-image" />
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={() => navigate('/properties')}>Browse Properties</button>
            <button className="btn btn-primary" onClick={() => navigate('/apply')}>Apply Now</button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section style={{ background: 'linear-gradient(135deg, #1a2332 0%, #2563eb 100%)', color: 'white', padding: '60px 0 40px' }}>
          <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
            <h1 style={{ fontSize: '2.4rem', fontWeight: '900', marginBottom: '16px', lineHeight: 1.2 }}>{h1}</h1>
            <p style={{ fontSize: '1.2rem', opacity: 0.9, marginBottom: '24px', maxWidth: '700px' }}>
              {description || `Find affordable shared housing in ${city}, ${state} — designed specifically for Australian Age Pensioners. Save 40–60% on rent and still receive Commonwealth Rent Assistance.`}
            </p>
            {stats.count > 0 && (
              <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', marginBottom: '28px' }}>
                <div><div style={{ fontSize: '2rem', fontWeight: '900' }}>{stats.count}</div><div style={{ opacity: 0.8, fontSize: '14px' }}>properties in region</div></div>
                <div><div style={{ fontSize: '2rem', fontWeight: '900' }}>${stats.avgRent}/wk</div><div style={{ opacity: 0.8, fontSize: '14px' }}>avg rent per bedroom</div></div>
                {stats.minNet > 0 && <div><div style={{ fontSize: '2rem', fontWeight: '900', color: '#86efac' }}>from ${stats.minNet}/wk</div><div style={{ opacity: 0.8, fontSize: '14px' }}>after CRA (singles)</div></div>}
              </div>
            )}
            <button className="btn btn-primary" style={{ fontSize: '18px', padding: '14px 32px' }} onClick={() => navigate('/apply')}>
              Apply for Membership — First 100 Just $20 &rarr;
            </button>
          </div>
        </section>

        {/* Properties */}
        {properties.length > 0 && (
          <section style={{ padding: '48px 0', background: '#f9fafb' }}>
            <div className="container" style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px' }}>
              <h2 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '8px' }}>Available Properties in {city}</h2>
              <p style={{ color: '#6b7280', marginBottom: '28px' }}>Shared housing properties suitable for Age Pensioners in the {city} region.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                {properties.map(p => {
                  const r = p.weekly_rent_per_person || 0;
                  const sCRA = Math.min(73.23, Math.max(0, 0.75 * (r - 74.10)));
                  return (
                    <div key={p.property_id} style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', cursor: 'pointer' }} onClick={() => navigate(`/properties/${p.property_id}`)}>
                      {p.images?.[0] && <img src={cloudinaryOptimize(p.images[0], 400)} alt={`${p.city} shared housing`} style={{ width: '100%', height: '180px', objectFit: 'cover' }} />}
                      <div style={{ padding: '16px' }}>
                        <h3 style={{ margin: '0 0 4px', fontSize: '16px' }}>{p.city} {p.state} {p.postcode || ''}</h3>
                        <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 8px' }}>{p.total_bedrooms} bedrooms · {p.property_type}</p>
                        <div style={{ color: '#4b5563', fontSize: '13px' }}>${Math.round(r)}/wk per bedroom</div>
                        {sCRA > 0 && <div style={{ color: '#b91c1c', fontWeight: '700' }}>Singles after CRA: ${Math.round(r - sCRA)}/wk</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ textAlign: 'center', marginTop: '28px' }}>
                <button className="btn btn-secondary" onClick={() => navigate(`/properties?state=${state}`)}>View All {state} Properties</button>
              </div>
            </div>
          </section>
        )}

        {/* Why HSS + CRA info */}
        <section style={{ padding: '48px 0' }}>
          <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '24px' }}>Why Age Pensioners in {city} Choose Shared Housing</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '40px' }}>
              {[
                { title: 'Reduce rent by 40–60%', desc: `The average rent in ${city} for a shared bedroom is far less than a solo rental. Most HSS members save over $10,000 per year.` },
                { title: 'Keep your CRA', desc: 'Age Pensioners in shared housing remain fully eligible for Commonwealth Rent Assistance through Centrelink — up to $73.23/wk for singles.' },
                { title: 'Safe & vetted', desc: 'Every HSS member is identity-verified, police-checked and reference-checked. You choose who you live with.' },
                { title: `Stay in ${city}`, desc: `Our ${stats.count > 0 ? stats.count : 'growing number of'} local properties mean you can stay close to family, services and community in ${city}.` },
              ].map(item => (
                <div key={item.title} style={{ background: '#eff6ff', borderRadius: '10px', padding: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1a2332', marginBottom: '8px' }}>{item.title}</h3>
                  <p style={{ fontSize: '14px', color: '#4b5563', margin: 0 }}>{item.desc}</p>
                </div>
              ))}
            </div>
            <div style={{ background: '#1a2332', borderRadius: '12px', padding: '28px 32px', textAlign: 'center', color: 'white' }}>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '8px' }}>Ready to find shared housing in {city}?</h2>
              <p style={{ opacity: 0.8, marginBottom: '20px' }}>Apply today — our Gold Envoy launch offer means the first 100 members pay just $20 for a lifetime membership.</p>
              <button className="btn btn-primary" style={{ fontSize: '17px', padding: '14px 32px' }} onClick={() => navigate('/apply')}>
                Start Your Application &rarr;
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LocationPage;
